import { getBackendHost } from './backendHost'

const MAX_QUEUE_SIZE = 100
const MAX_BATCH_SIZE = 20
const FLUSH_INTERVAL_MS = 5000
const RETRY_INTERVAL_MS = 15000
const QUEUE_STORAGE_KEY = 'sayable_observability_queue'
const FAILURE_STATUSES = new Set(['failed', 'rejected', 'unavailable'])
const SENSITIVE_DETAIL_KEY = /(authorization|cookie|password|passwd|secret|token|credential|proof|nonce|api[_-]?key|audio|transcript|prompt|argument)/i
export const MAX_BEARER_TOKEN_LENGTH = 4096

let eventQueue = []
let flushTimer = null
let flushInFlight = null
let globalCleanup = null
const observedErrors = typeof WeakSet === 'function' ? new WeakSet() : null

export const isTransportSafeAuthToken = token => (
  typeof token === 'string'
  && token.length > 0
  && token.length <= MAX_BEARER_TOKEN_LENGTH
)

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const getSessionId = () => {
  const key = 'sayable_observability_session'
  let sessionId = sessionStorage.getItem(key)
  if (!sessionId) {
    sessionId = createId()
    sessionStorage.setItem(key, sessionId)
  }
  return sessionId
}

const tokenFingerprint = token => {
  let hash = 2166136261
  for (let index = 0; index < token.length; index += 1) {
    hash ^= token.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

const actorKeyForToken = token => {
  if (!token) return 'anonymous'
  try {
    const payloadSegment = token.split('.')[1]
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')))
    const actorId = payload.id || payload.user_id || payload.email
    if (actorId) return `actor:${String(actorId).slice(0, 160)}`
  } catch (_error) {
    // A fingerprint still prevents cross-account delivery for an unreadable token.
  }
  return `token:${tokenFingerprint(token)}`
}

const currentActorKey = () => actorKeyForToken(localStorage.getItem('jwt_authorization'))

const safeMetadata = (data) => {
  if (!data || typeof data !== 'object') return {}
  const allowedKeys = [
    'page',
    'section',
    'source',
    'component',
    'status',
    'value',
    'location',
    'target',
    'element',
    'role',
    'path',
    'operation',
    'method',
    'requestOrigin',
    'statusCode',
    'errorCode',
    'errorType',
    'networkCode',
    'responseType',
    'responseTraceId',
    'requestId',
    'hasResponse',
    'credentialAttached',
    'mediaErrorCode',
    'networkState',
    'readyState',
    'online',
    'line',
    'column',
    'onboardingVersion',
    'stepKey',
    'durationMs',
    'retryCount',
    'goal',
    'flowStatus'
  ]
  return allowedKeys.reduce((result, key) => {
    const value = data[key]
    if (['string', 'number', 'boolean'].includes(typeof value)) {
      result[key] = typeof value === 'string' ? value.slice(0, 240) : value
    }
    return result
  }, {})
}

const safeStackTrace = (value) => {
  if (typeof value !== 'string') return undefined
  return value
    .replace(/https?:\/\/[^\s)]+/g, rawUrl => {
      try {
        const url = new URL(rawUrl)
        return `${url.origin}${url.pathname}`
      } catch (_error) {
        return '[redacted-url]'
      }
    })
    .slice(0, 12000)
}

const redactSensitiveText = value => String(value)
  .replace(/\bBearer\s+[a-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
  .replace(/((?:authorization|password|passwd|secret|token|credential|proof|nonce|api[_-]?key)\s*["']?\s*[:=]\s*["']?)[^"',;\s}&]+/gi, '$1[redacted]')

const parseDiagnosticPayload = value => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (_error) {
    return value
  }
}

const diagnosticText = value => redactSensitiveText(value)
  .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
  .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&#39;|&apos;/gi, "'")
  .replace(/&quot;/gi, '"')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, 2000)

const diagnosticResponseBody = (value, contentType = '') => {
  if (value && typeof value === 'object') return value
  if (String(contentType).toLowerCase().includes('json')) return parseDiagnosticPayload(value)
  if (value === null || value === undefined || value === '') return null
  return diagnosticText(value) || `[empty ${String(contentType || 'response').slice(0, 120)} response]`
}

const safeDiagnosticValue = (value, key = '', depth = 0) => {
  if (SENSITIVE_DETAIL_KEY.test(key)) return '[redacted]'
  if (value === null || value === undefined) return value ?? null
  if (depth >= 5) return '[truncated]'
  if (typeof value === 'string') return redactSensitiveText(value).slice(0, 4000)
  if (['number', 'boolean'].includes(typeof value)) return value
  if (Array.isArray(value)) return value.slice(0, 25).map(item => safeDiagnosticValue(item, '', depth + 1))
  if (typeof value === 'object') {
    return Object.entries(value).slice(0, 40).reduce((result, [itemKey, itemValue]) => {
      result[String(itemKey).slice(0, 100)] = safeDiagnosticValue(itemValue, itemKey, depth + 1)
      return result
    }, {})
  }
  return String(value).slice(0, 500)
}

const safeDiagnostics = value => safeDiagnosticValue(value)

const persistQueue = () => {
  try {
    sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(eventQueue))
  } catch (_error) {
    // Delivery still works when storage is unavailable or full.
  }
}

const restoreQueue = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(QUEUE_STORAGE_KEY) || '[]')
    if (Array.isArray(stored)) {
      const actorKey = currentActorKey()
      eventQueue = stored.filter(item => item?._actorKey === actorKey).slice(-MAX_QUEUE_SIZE).map(item => ({ ...item, eventId: item.eventId || createId() }))
      persistQueue()
    }
  } catch (_error) {
    eventQueue = []
  }
}

const scheduleFlush = (delay = FLUSH_INTERVAL_MS) => {
  if (flushTimer) return
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    flushTelemetry()
  }, delay)
}

export function flushTelemetry() {
  if (flushTimer) {
    window.clearTimeout(flushTimer)
    flushTimer = null
  }
  const storedToken = localStorage.getItem('jwt_authorization')
  const actorKey = actorKeyForToken(storedToken)
  eventQueue = eventQueue.filter(item => item._actorKey === actorKey)
  persistQueue()
  if (flushInFlight) return flushInFlight
  if (!eventQueue.length) return Promise.resolve()

  const queuedEvents = eventQueue.slice(0, MAX_BATCH_SIZE)
  const events = queuedEvents.map(({ _actorKey, ...event }) => event)
  const deliveryTraceId = createId()
  const headers = {
    'Content-Type': 'application/json',
    'X-Trace-ID': deliveryTraceId,
    'X-Session-ID': getSessionId()
  }
  if (isTransportSafeAuthToken(storedToken)) headers.Authorization = `Bearer ${storedToken}`

  flushInFlight = fetch(`${getBackendHost()}/observability/events`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ events }),
    keepalive: true,
    credentials: 'omit'
  })
    .then(response => {
      if (!response.ok) throw new Error(`Telemetry delivery returned HTTP ${response.status}`)
      const delivered = new Set(queuedEvents)
      eventQueue = eventQueue.filter(item => !delivered.has(item))
      persistQueue()
    })
    .catch(() => {
      persistQueue()
      scheduleFlush(RETRY_INTERVAL_MS)
    })
    .finally(() => {
      flushInFlight = null
      if (eventQueue.length && !flushTimer) scheduleFlush()
    })

  return flushInFlight
}

export function logEvent(eventMessage) {
  const event = eventMessage?.event || {}
  if (!event.label) return
  // Platform administration navigation is not customer usage. Keep actions and errors.
  if (['PageView', 'SectionView'].includes(event.label)) {
    const path = event.data?.path || window.location.pathname
    if (/^\/(operations|admin)(?:\/|[?#]|$)/i.test(path)) return
  }
  const actorKey = currentActorKey()
  const actorQueue = eventQueue.filter(item => item._actorKey === actorKey)
  if (actorQueue.length !== eventQueue.length) {
    eventQueue = actorQueue
    persistQueue()
  }
  const queuedEvent = {
    _actorKey: actorKey,
    eventId: createId(),
    name: String(event.label).slice(0, 160),
    status: FAILURE_STATUSES.has(event.status) ? event.status : 'succeeded',
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    traceId: event.traceId ? String(event.traceId).slice(0, 128) : createId(),
    metadata: safeMetadata(event.data)
  }
  const stackTrace = safeStackTrace(event.stackTrace)
  if (stackTrace) queuedEvent.stackTrace = stackTrace
  if (event.diagnostics) queuedEvent.diagnostics = safeDiagnostics(event.diagnostics)

  if (['PageView', 'SectionView'].includes(queuedEvent.name)) {
    const duplicate = eventQueue.slice(-10).find(item => (
      item.name === queuedEvent.name
      && item.metadata?.page === queuedEvent.metadata?.page
      && item.metadata?.section === queuedEvent.metadata?.section
      && Date.now() - new Date(item.timestamp).getTime() < 2000
    ))
    if (duplicate) {
      duplicate.metadata = { ...duplicate.metadata, ...queuedEvent.metadata }
      persistQueue()
      return
    }
  }

  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    if (!FAILURE_STATUSES.has(queuedEvent.status)) return
    const succeededIndex = eventQueue.findIndex(item => item.status === 'succeeded')
    if (succeededIndex >= 0) eventQueue.splice(succeededIndex, 1)
    else eventQueue.shift()
  }
  eventQueue.push(queuedEvent)
  persistQueue()
  if (eventQueue.length >= MAX_BATCH_SIZE) flushTelemetry()
  else scheduleFlush()
}

const requestPath = (url) => {
  if (!url) return 'unknown'
  const rawUrl = typeof url === 'string' ? url : url.url
  if (typeof rawUrl !== 'string') return 'unknown'
  try {
    return new URL(rawUrl, window.location.origin).pathname.slice(0, 240)
  } catch (_error) {
    return rawUrl.split(/[?#]/, 1)[0].slice(0, 240)
  }
}

const requestOrigin = (url) => {
  const rawUrl = typeof url === 'string' ? url : url?.url
  if (typeof rawUrl !== 'string') return 'unknown'
  try {
    return new URL(rawUrl, window.location.origin).origin.slice(0, 240)
  } catch (_error) {
    return 'unknown'
  }
}

const headerValue = (headers, name) => {
  if (!headers) return ''
  if (typeof headers.get === 'function') return headers.get(name) || ''
  const loweredName = name.toLowerCase()
  const match = Object.keys(headers).find(key => key.toLowerCase() === loweredName)
  return match ? headers[match] : ''
}

const tracedFetchInit = (input, init, traceId, sessionId) => {
  if (typeof Headers === 'undefined') {
    return {
      ...(init || {}),
      headers: { ...(init?.headers || {}), 'X-Trace-ID': traceId, 'X-Session-ID': sessionId }
    }
  }
  const headers = new Headers(
    typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined
  )
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value))
  headers.set('X-Trace-ID', traceId)
  headers.set('X-Session-ID', sessionId)
  return { ...(init || {}), headers }
}

const isSayableBackendRequest = input => {
  const rawUrl = typeof input === 'string' ? input : input?.url
  if (!rawUrl || !process.env.REACT_APP_BACKEND_HOST) return false
  try {
    const candidate = new URL(rawUrl, window.location.origin)
    const backend = new URL(process.env.REACT_APP_BACKEND_HOST, window.location.origin)
    const backendPath = backend.pathname.replace(/\/$/, '')
    const withinBackendPath = candidate.pathname === backendPath || candidate.pathname.startsWith(`${backendPath}/`)
    const usesConfiguredBackend = candidate.origin === backend.origin && withinBackendPath
    const usesSameOriginProxy = candidate.origin === window.location.origin && withinBackendPath
    return (usesConfiguredBackend || usesSameOriginProxy) && !candidate.pathname.endsWith('/observability/events')
  } catch (_error) {
    return false
  }
}

const hasCredential = (headers) => {
  if (!headers) return false
  if (typeof headers.get === 'function') return Boolean(headers.get('Authorization'))
  return Boolean(headers.Authorization || headers.authorization)
}

const currentPage = () => window.location.pathname.slice(0, 240)

const markObserved = (error) => {
  if (!observedErrors || !error || (typeof error !== 'object' && typeof error !== 'function')) return false
  if (observedErrors.has(error)) return true
  observedErrors.add(error)
  return false
}

export const resourceFailureMetadata = target => {
  if (!target || target === window) return null
  const element = String(target.tagName || 'unknown').toLowerCase()
  const isMedia = element === 'audio' || element === 'video'
  const rawMediaSource = target.getAttribute?.('src')
  const attributeSource = rawMediaSource || target.getAttribute?.('href') || ''
  const source = isMedia
    ? (target.currentSrc || attributeSource)
    : (attributeSource || target.src || target.href || '')

  // Clearing a media element intentionally may emit an error event. An empty
  // source attribute or missing MediaError is not a failed resource request.
  if (!source || (isMedia && (rawMediaSource === '' || !target.error))) return null

  return {
    page: currentPage(),
    path: requestPath(source),
    source: 'resource',
    element,
    ...(isMedia ? {
      mediaErrorCode: Number(target.error?.code || 0),
      networkState: Number(target.networkState || 0),
      readyState: Number(target.readyState || 0)
    } : {})
  }
}

export function logApiError({ page, operation, error }) {
  if (markObserved(error)) return
  const response = error?.response
  const config = error?.config || {}
  const responseHeaders = response?.headers || {}
  const apiError = response?.data?.error
  const requestContext = config.sayableObservability || {}
  const responseTraceId = headerValue(responseHeaders, 'x-trace-id')
  const traceId = responseTraceId || requestContext.traceId
  const requestId = headerValue(responseHeaders, 'x-request-id')
    || headerValue(responseHeaders, 'x-amzn-requestid')
    || headerValue(responseHeaders, 'x-amzn-trace-id')
  const contentType = String(headerValue(responseHeaders, 'content-type'))

  logEvent({
    event: {
      label: 'ApiRequestFailed',
      status: 'failed',
      traceId,
      stackTrace: error?.stack,
      diagnostics: {
        request: {
          traceId,
          method: String(config.method || 'unknown').toUpperCase(),
          origin: requestOrigin(config.url),
          path: requestPath(config.url),
          query: config.params,
          body: parseDiagnosticPayload(config.data)
        },
        response: {
          status: Number.isInteger(response?.status) ? response.status : 0,
          contentType,
          traceId: responseTraceId || null,
          requestId: requestId || null,
          body: diagnosticResponseBody(response?.data, contentType)
        }
      },
      data: {
        page: page || requestContext.page || currentPage(),
        operation: operation || requestPath(config.url),
        path: requestPath(config.url),
        method: String(config.method || 'unknown').toUpperCase(),
        requestOrigin: requestOrigin(config.url),
        statusCode: Number.isInteger(response?.status) ? response.status : 0,
        errorCode: typeof apiError?.code === 'string' ? apiError.code : '',
        errorType: error?.name || 'Error',
        networkCode: typeof error?.code === 'string' ? error.code : '',
        responseType: contentType.slice(0, 120),
        ...(responseTraceId ? { responseTraceId } : {}),
        ...(requestId ? { requestId } : {}),
        hasResponse: Boolean(response),
        credentialAttached: hasCredential(config.headers) || Boolean(parseDiagnosticPayload(config.data)?.jwt_token),
        online: typeof navigator !== 'undefined' ? navigator.onLine : true
      }
    }
  })
}

export function logSectionView(page, section, source = 'interaction') {
  if (!section) return
  logEvent({
    event: {
      label: 'SectionView',
      data: { page, section, source, path: currentPage() }
    }
  })
}

export function logFrontendError({ error, source = 'runtime', component = '', stackTrace }) {
  if (markObserved(error)) return
  logEvent({
    event: {
      label: 'FrontendError',
      status: 'failed',
      stackTrace: stackTrace || error?.stack,
      data: {
        page: currentPage(),
        path: currentPage(),
        source,
        component,
        errorType: error?.name || typeof error || 'Error'
      }
    }
  })
}

const logFetchFailure = ({ input, init, response, responseBody, error, stackTrace, traceId, page }) => {
  const path = requestPath(input)
  if (path.endsWith('/observability/events')) return
  const responseTraceId = headerValue(response?.headers, 'x-trace-id')
  const correlatedTraceId = responseTraceId || traceId
  const requestId = headerValue(response?.headers, 'x-request-id')
    || headerValue(response?.headers, 'x-amzn-requestid')
    || headerValue(response?.headers, 'x-amzn-trace-id')
  const contentType = String(headerValue(response?.headers, 'content-type'))
  logEvent({
    event: {
      label: 'ApiRequestFailed',
      status: 'failed',
      traceId: correlatedTraceId,
      stackTrace: error?.stack || stackTrace,
      diagnostics: {
        request: {
          traceId: correlatedTraceId,
          method: String(init?.method || 'GET').toUpperCase(),
          origin: requestOrigin(input),
          path,
          body: parseDiagnosticPayload(init?.body)
        },
        response: {
          status: Number.isInteger(response?.status) ? response.status : 0,
          contentType,
          traceId: responseTraceId || null,
          requestId: requestId || null,
          body: responseBody
        }
      },
      data: {
        page: page || currentPage(),
        operation: path,
        path,
        method: String(init?.method || 'GET').toUpperCase(),
        requestOrigin: requestOrigin(input),
        statusCode: Number.isInteger(response?.status) ? response.status : 0,
        errorType: error?.name || 'FetchError',
        networkCode: '',
        responseType: contentType.slice(0, 120),
        ...(responseTraceId ? { responseTraceId } : {}),
        ...(requestId ? { requestId } : {}),
        hasResponse: Boolean(response),
        credentialAttached: hasCredential(init?.headers),
        online: typeof navigator !== 'undefined' ? navigator.onLine : true
      }
    }
  })
}

const captureFetchResponseFailure = async ({ input, init, response, stackTrace, traceId, page }) => {
  let responseBody = null
  try {
    const text = await response.clone().text()
    responseBody = diagnosticResponseBody(text.slice(0, 12000), response.headers?.get?.('content-type'))
  } catch (_error) {
    responseBody = '[unavailable]'
  }
  logFetchFailure({ input, init, response, responseBody, stackTrace, traceId, page })
}

export function installFrontendErrorCapture(axiosClient) {
  if (globalCleanup || typeof window === 'undefined') return globalCleanup || (() => {})

  const axiosRequestInterceptor = axiosClient?.interceptors?.request?.use(config => {
    if (!isSayableBackendRequest(config?.url)) return config
    const traceId = createId()
    const sessionId = getSessionId()
    if (typeof config.headers?.set === 'function') config.headers.set('X-Trace-ID', traceId)
    else config.headers = { ...(config.headers || {}), 'X-Trace-ID': traceId }
    if (typeof config.headers?.set === 'function') config.headers.set('X-Session-ID', sessionId)
    else config.headers = { ...(config.headers || {}), 'X-Session-ID': sessionId }
    config.sayableObservability = { traceId, sessionId, page: currentPage() }
    return config
  })
  const axiosInterceptor = axiosClient?.interceptors?.response?.use(
    response => response,
    error => {
      logApiError({ error })
      return Promise.reject(error)
    }
  )
  const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null
  const observedFetch = nativeFetch && ((input, init) => {
    const requestStack = new Error('Fetch request initiated').stack
    const observeRequest = isSayableBackendRequest(input)
    const traceId = observeRequest ? createId() : null
    const page = observeRequest ? currentPage() : null
    const requestInit = observeRequest ? tracedFetchInit(input, init, traceId, getSessionId()) : init
    return nativeFetch(input, requestInit).then(
    response => {
      if (observeRequest && response.type !== 'opaque' && !response.ok) {
        captureFetchResponseFailure({ input, init: requestInit, response, stackTrace: requestStack, traceId, page })
      }
      return response
    },
    error => {
      if (observeRequest) logFetchFailure({ input, init: requestInit, error, stackTrace: requestStack, traceId, page })
      throw error
    }
    )
  })
  if (observedFetch) window.fetch = observedFetch

  const handleRuntimeError = event => {
    if (event.error) {
      logFrontendError({ error: event.error, source: 'window' })
      return
    }
    const target = event.target
    if (target && target !== window) {
      const metadata = resourceFailureMetadata(target)
      if (!metadata) return
      logEvent({
        event: {
          label: 'ResourceLoadFailed',
          status: 'failed',
          data: metadata
        }
      })
    }
  }
  const handleUnhandledRejection = event => {
    const reason = event.reason instanceof Error ? event.reason : new Error('Unhandled promise rejection')
    logFrontendError({ error: reason, source: 'unhandledrejection' })
  }
  const handleOnline = () => flushTelemetry()
  const handlePageHide = () => flushTelemetry()

  window.addEventListener('error', handleRuntimeError, true)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  window.addEventListener('online', handleOnline)
  window.addEventListener('pagehide', handlePageHide)

  globalCleanup = () => {
    if (axiosRequestInterceptor !== undefined) axiosClient?.interceptors?.request?.eject(axiosRequestInterceptor)
    if (axiosInterceptor !== undefined) axiosClient?.interceptors?.response?.eject(axiosInterceptor)
    if (observedFetch && window.fetch === observedFetch) window.fetch = nativeFetch
    window.removeEventListener('error', handleRuntimeError, true)
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('pagehide', handlePageHide)
    globalCleanup = null
  }
  return globalCleanup
}

if (typeof window !== 'undefined') {
  restoreQueue()
  if (eventQueue.length) scheduleFlush()
}
